import { useQuery } from '@tanstack/react-query';
import { useSupabaseClient } from './use-supabase';

/**
 * Fine-grained access check for modules + features.
 *
 * A user may be granted:
 *   - scope='module'  → access to every feature in the module
 *   - scope='feature' → access to one named feature only
 *
 * Usage inside a module:
 *   const access = useModuleAccess('marketing-spend');
 *   if (!access.canView('raw-data')) return null;
 */

interface ModuleAccessResult {
  isLoading: boolean;
  /** User has any kind of access to this module (module-level or any feature). */
  hasAny: boolean;
  /** User has module-level access — implies access to every feature. */
  hasModule: boolean;
  /** Can the user view this specific feature? Module-level grant also returns true. */
  canView: (featureKey: string) => boolean;
}

export function useModuleAccess(moduleSlug: string): ModuleAccessResult {
  const supabase = useSupabaseClient();

  const { data, isLoading } = useQuery({
    queryKey: ['module-access', 'current-user', moduleSlug],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return { moduleGrant: false, featureGrants: new Set<string>() };
      }

      // 1. Resolve the module id from slug.
      const { data: moduleRows } = await supabase
        .from('modules')
        .select('id')
        .eq('slug', moduleSlug)
        .limit(1);
      const moduleId = (moduleRows as Array<{ id: string }> | null)?.[0]?.id;
      if (!moduleId) {
        return { moduleGrant: false, featureGrants: new Set<string>() };
      }

      // 2. All access rows for this user + module.
      const { data: accessRows } = await supabase
        .from('module_access')
        .select('scope, feature_id')
        .eq('user_id', userData.user.id)
        .eq('module_id', moduleId);
      const rows = (accessRows as Array<{ scope: string; feature_id: string | null }> | null) ?? [];

      const moduleGrant = rows.some((r) => r.scope === 'module');
      const featureIds = new Set(
        rows.filter((r) => r.scope === 'feature' && r.feature_id).map((r) => r.feature_id as string),
      );

      // 3. Resolve feature_ids back to keys so the UI can gate by key.
      const { data: featureRows } =
        featureIds.size > 0
          ? await supabase
              .from('module_features')
              .select('id, key')
              .eq('module_id', moduleId)
          : { data: [] };
      const byKey = new Set<string>();
      for (const f of (featureRows as Array<{ id: string; key: string }> | null) ?? []) {
        if (featureIds.has(f.id)) byKey.add(f.key);
      }

      return { moduleGrant, featureGrants: byKey };
    },
    staleTime: 60_000,
  });

  const hasModule = data?.moduleGrant ?? false;
  const featureGrants = data?.featureGrants ?? new Set<string>();

  return {
    isLoading,
    hasModule,
    hasAny: hasModule || featureGrants.size > 0,
    canView: (key: string) => hasModule || featureGrants.has(key),
  };
}
